import React, { ReactElement } from 'react';
import useStyles from './styles';
import { useHistory } from 'react-router';
import TranslatedText from '../../../components/atoms/translated_text';
import Configuration, { EnumSchemeTypes, Translations } from './configuration';
import * as RegisterSelectors from '../../../redux/reducers/register_reducer/selectors';
import {
  AppGroupText,
  AppMultipleSelect,
  AppSelect,
  AppSwitch,
} from '../../../components/molecules/form_controls';
import { Control, Controller, useForm } from 'react-hook-form';
import _ from 'lodash';
import NavigationConfig from '../../../navigation/config';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import NONEUForm from './NON_EU_Form';
import IOSSForm from './IOSS_Form';
import EUForm from './EU_Form';
import { Link } from 'react-router-dom';
import { StateStatus } from '../../../redux/utils/common';
import { useDispatch } from 'react-redux';
import RegisterActions from '../../../redux/reducers/register_reducer/actions';
import { Roles, AuthRoles } from '../../../config';
import * as DateUtils from 'utils/dates';

interface IProps {
  isAddApplicantPage?: boolean;
  role?: Roles | null;
}

const defaultProps: IProps = {
  isAddApplicantPage: false,
  role: null,
};
interface IFormData {
  schemeType: null | EnumSchemeTypes;
  isIntermediary: boolean;
}

// add types or interface
const defaultValues = {
  schemeType: null,
  isIntermediary: false,
};

export default function RegisterForm(
  props: IProps = defaultProps
): ReactElement {
  // console.log('Render RegisterForm:');
  const classes = useStyles();
  const { setValue, watch, errors, control, getValues } = useForm<IFormData>({
    defaultValues,
  });

  const isAddApplicantPage: boolean | undefined = props.isAddApplicantPage;
  const ROLE = props.role;

  const dispatch = useDispatch();
  const history = useHistory();
  let watchSchemeType = watch('schemeType');
  let watchIsIntermediary = watch('isIntermediary');

  const { status, data } = RegisterSelectors.useGetSchemeTypes();

  let elements = Array.isArray(data)
    ? data.map((element, index) => ({
        id: element.code,
        label: element.description,
      }))
    : [];

  React.useEffect(() => {
    if (status === StateStatus.Success) {
      if (elements.length > 0) {
        setValue('schemeType', elements[0].id);
        if (ROLE === AuthRoles.Agent) setValue('schemeType', elements[2].id);
      }
    }
  }, [status]);

  const renderForm = () => {
    switch (watchSchemeType) {
      case EnumSchemeTypes.eu:
        return <EUForm onSubmit={onSubmit} />;
      case EnumSchemeTypes.ioss:
        if (watchIsIntermediary) {
          return <EUForm onSubmit={onSubmit} />;
        } else {
          return <IOSSForm onSubmit={onSubmit} />;
        }
      case EnumSchemeTypes.non_eu:
        return <NONEUForm onSubmit={onSubmit} />;
    }
    return null;
  };

  const onSubmit = (_data: any) => {
    dispatch(
      RegisterActions.registerUserAction(
        {
          ..._data,
          ...(_data.certicifateDate && {
            certicifateDate: DateUtils.DateInQueryFormat(_data.certicifateDate),
          }),
          schemeType: getValues('schemeType'),
          isIntermediary: getValues('isIntermediary'),
        },
        history,
        isAddApplicantPage as boolean
      )
    );
  };

  return (
    <div className={classes.form}>
      <AppSelect
        variant={'outlined'}
        InputLabelProps={{
          classes: {
            root: classes.inputLabel,
            focused: classes.inputLabelFocus,
          },
        }}
        InputProps={{
          classes: {
            focused: classes.notchedOutlineFocus,
            notchedOutline: classes.notchedOutline,
          } as any,
          className: classes.input,
        }}
        SelectProps={{
          classes: { icon: classes.selectIcon },
        }}
        className={classes.formField}
        fullWidth
        label="OSS Scheme"
        name={'schemeType'}
        required
        error={'schemeType' in errors}
        control={control}
        autoComplete="schemeType"
        rules={{ required: true }}
        icon={{
          position: 'left',
          Component: <ClearAllIcon className={classes.inputIcon} />,
        }}
        helperText={' '}
        options={elements}
        disabled={isAddApplicantPage && ROLE == AuthRoles.Agent}
      />
      {watchSchemeType === EnumSchemeTypes.ioss && ROLE != AuthRoles.Agent && (
        <div style={{ marginBottom: '1rem' }}>
          <AppSwitch
            name="isIntermediary"
            required
            color="secondary"
            control={control}
            rules={{ required: true }}
            label="Is Intermediary"
          />
        </div>
      )}
      {renderForm()}
      {!isAddApplicantPage && (
        <div className={classes.layout_links}>
          <TranslatedText textMap={Translations.already_have_an_account} />
          <Link
            className={classes.link}
            /*className="font-medium"*/ to={NavigationConfig.loginPage().path}
          >
            <TranslatedText textMap={Translations.back_to_login} />
          </Link>
        </div>
      )}
    </div>
  );
}
